/**
 * CVSS Vector Calculator - Calculador dinámico de vector CVSS 3.1
 * 
 * Evalúa métricas CVSS dinámicamente basándose en evidencias del evento detectado
 * y calcula el vector CVSS 3.1 según la especificación oficial FIRST.org.
 */

export class CVSSVectorCalculator {
	calculateVector(eventType, variant = null, context = {}) {
		const av = this.evaluateAttackVector(eventType);
		const ac = this.evaluateAttackComplexity(eventType, variant, context);
		const pr = this.evaluatePrivilegesRequired(eventType, context);
		const ui = this.evaluateUserInteraction(eventType, variant, context);
		const s = this.evaluateScope(eventType, variant, context);
		const c = this.evaluateConfidentiality(eventType, variant, context);
		const i = this.evaluateIntegrity(eventType, variant, context);
		const a = this.evaluateAvailability(eventType, variant, context);

		return `CVSS:3.1/AV:${av}/AC:${ac}/PR:${pr}/UI:${ui}/S:${s}/C:${c}/I:${i}/A:${a}`;
	}

	evaluateAttackVector(eventType) {
		return 'N';
	}

	evaluateAttackComplexity(eventType, variant, context) {
		return 'L';
	}

	evaluatePrivilegesRequired(eventType, context) {
		const url = context.url || '';
		const urlLower = String(url).toLowerCase();

		if (urlLower.includes('/admin') || urlLower.includes('/administrator') ||
			urlLower.includes('/manage') || urlLower.includes('/config') ||
			urlLower.includes('/settings') || urlLower.includes('/grant') ||
			urlLower.includes('/permission')) {
			return 'H';
		}

		if (urlLower.includes('/user') || urlLower.includes('/account') ||
			urlLower.includes('/profile') || urlLower.includes('/dashboard')) {
			return 'L';
		}

		return 'N';
	}

	evaluateUserInteraction(eventType, variant, context) {
		if (eventType === 'xss') {
			if (variant === 'script_tag' || variant === 'javascript_protocol' || variant === 'cookie_theft') {
				return 'N';
			}
			if (variant === 'event_handler') {
				return 'R';
			}
			return 'R';
		}

		if (eventType === 'csrf') {
			return 'R';
		}

		// Injection: user sends malicious request
		if (eventType === 'sql_injection' || eventType === 'command_injection' || eventType === 'path_traversal') {
			return 'R';
		}

		return 'R';
	}

	evaluateScope(eventType, variant, context) {
		if (eventType === 'csrf') {
			const url = context.url || '';
			const urlLower = String(url).toLowerCase();

			if (variant === 'financial_operation' || variant === 'sensitive_endpoint') {
				return 'C';
			}

			if (urlLower.includes('payment') || urlLower.includes('transfer') ||
				urlLower.includes('billing') || urlLower.includes('financial') ||
				urlLower.includes('transaction') || urlLower.includes('purchase')) {
				return 'C';
			}
		}

		// Injection: scope unchanged by default (impact on same component)
		if (eventType === 'sql_injection' || eventType === 'command_injection' || eventType === 'path_traversal') {
			return 'U';
		}

		return 'U';
	}

	evaluateConfidentiality(eventType, variant, context) {
		if (eventType === 'xss') {
			const content = context.content || '';
			const contentLower = String(content).toLowerCase();

			if (variant === 'cookie_theft' || contentLower.includes('cookie') || contentLower.includes('document.cookie')) {
				return 'H';
			}

			if (contentLower.includes('localstorage') || contentLower.includes('sessionstorage') ||
				contentLower.includes('getitem')) {
				return 'H';
			}

			return 'L';
		}

		if (eventType === 'csrf') {
			const url = context.url || '';
			const urlLower = String(url).toLowerCase();

			if (variant === 'financial_operation' || variant === 'sensitive_endpoint') {
				return 'H';
			}

			if (urlLower.includes('payment') || urlLower.includes('account') ||
				urlLower.includes('profile') || urlLower.includes('personal')) {
				return 'H';
			}

			return 'H';
		}

		// Injection: data exposure / read impact
		if (eventType === 'sql_injection') {
			return 'H';
		}
		if (eventType === 'command_injection') {
			return 'H';
		}
		if (eventType === 'path_traversal') {
			return 'L';
		}

		return 'L';
	}

	evaluateIntegrity(eventType, variant, context) {
		if (eventType === 'xss') {
			const content = context.content || '';
			const contentLower = String(content).toLowerCase();

			if (variant === 'cookie_theft' || contentLower.includes('cookie') || contentLower.includes('document.cookie')) {
				return 'H';
			}

			if (contentLower.includes('innerhtml') || contentLower.includes('document.write') ||
				contentLower.includes('dom') || contentLower.includes('modify')) {
				return 'H';
			}

			return 'L';
		}

		if (eventType === 'csrf') {
			const method = context.method || '';
			const url = context.url || '';
			const urlLower = String(url).toLowerCase();

			if (method === 'POST' || method === 'PUT' || method === 'DELETE') {
				if (variant === 'financial_operation' || variant === 'sensitive_endpoint') {
					return 'H';
				}

				if (urlLower.includes('delete') || urlLower.includes('update') ||
					urlLower.includes('modify') || urlLower.includes('change')) {
					return 'H';
				}

				return 'H';
			}

			return 'L';
		}

		// Injection: data/modification impact
		if (eventType === 'sql_injection') {
			return 'H';
		}
		if (eventType === 'command_injection') {
			return 'H';
		}
		if (eventType === 'path_traversal') {
			return 'L';
		}

		return 'L';
	}

	evaluateAvailability(eventType, variant, context) {
		if (eventType === 'xss') {
			const content = context.content || '';
			const contentLower = String(content).toLowerCase();

			if (contentLower.includes('while') || contentLower.includes('for') ||
				contentLower.includes('infinite') || contentLower.includes('loop') ||
				contentLower.includes('alert') && contentLower.includes('1000')) {
				return 'H';
			}

			return 'N';
		}

		if (eventType === 'csrf') {
			const method = context.method || '';
			const url = context.url || '';
			const urlLower = String(url).toLowerCase();

			if (method === 'DELETE' && (variant === 'sensitive_endpoint' || urlLower.includes('delete'))) {
				return 'H';
			}

			if (variant === 'financial_operation') {
				return 'H';
			}

			return 'N';
		}

		// Injection: availability impact (SQLi DoS, RCE can disrupt)
		if (eventType === 'sql_injection' && (variant === 'sleep_benchmark' || variant === 'termination_drop')) {
			return 'L';
		}
		if (eventType === 'command_injection') {
			return 'H';
		}
		if (eventType === 'path_traversal') {
			return 'N';
		}

		return 'N';
	}

	calculateScore(vector) {
		if (!vector || typeof vector !== 'string') {
			return 0.0;
		}

		const metrics = this.parseVector(vector);
		if (!metrics) {
			return 0.0;
		}

		const avValues = { 'N': 0.85, 'A': 0.62, 'L': 0.55, 'P': 0.2 };
		const acValues = { 'L': 0.77, 'H': 0.44 };
		const prValues = { 'N': 0.85, 'L': 0.62, 'H': 0.27 };
		const uiValues = { 'N': 0.85, 'R': 0.62 };
		const cValues = { 'N': 0, 'L': 0.22, 'H': 0.56 };
		const iValues = { 'N': 0, 'L': 0.22, 'H': 0.56 };
		const aValues = { 'N': 0, 'L': 0.22, 'H': 0.56 };

		const av = avValues[metrics.AV] || 0.85;
		const ac = acValues[metrics.AC] || 0.77;
		const pr = prValues[metrics.PR] || 0.85;
		const ui = uiValues[metrics.UI] || 0.62;
		const c = cValues[metrics.C] || 0;
		const i = iValues[metrics.I] || 0;
		const a = aValues[metrics.A] || 0;
		const s = metrics.S === 'C' ? 1 : 0;

		const iscBase = 1 - ((1 - c) * (1 - i) * (1 - a));

		if (iscBase <= 0) {
			return 0.0;
		}

		let impact = 0;
		if (s === 0) {
			impact = 6.42 * iscBase;
		} else {
			impact = 7.52 * (iscBase - 0.029) - 3.25 * Math.pow(iscBase - 0.02, 15);
		}

		if (impact <= 0) {
			return 0.0;
		}

		const exploitability = 8.22 * av * ac * pr * ui;

		let baseScore = 0;
		if (s === 0) {
			baseScore = Math.min(impact + exploitability, 10);
		} else {
			baseScore = Math.min(1.08 * (impact + exploitability), 10);
		}

		baseScore = Math.ceil(baseScore * 10) / 10;

		return Math.min(baseScore, 10.0);
	}

	parseVector(vector) {
		const metrics = {};
		const parts = vector.split('/');

		for (const part of parts) {
			if (part.startsWith('CVSS:3.1')) {
				continue;
			}

			const [key, value] = part.split(':');
			if (key && value) {
				metrics[key] = value;
			}
		}

		const required = ['AV', 'AC', 'PR', 'UI', 'S', 'C', 'I', 'A'];
		for (const req of required) {
			if (!metrics[req]) {
				return null;
			}
		}

		return metrics;
	}
}

export default CVSSVectorCalculator;
